import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axiosConfig';
import AuthContext from '../contexts/AuthContext';
import { useModal } from '../contexts/ModalContext';

import ImageViewerModal from '../components/ImageViewerModal';
import { formatDescription } from '../utils/reportDescription';

function InspectionDetailPage() {
    const [report, setReport] = useState(null);
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const { showAlert, showConfirm } = useModal();
    // OWNER / SUPER_ADMIN 은 권한 검사를 통과한다. 나머지는 권한 설정에서
    // '점검 기록 삭제·복구' 를 받은 사람이다.
    const canDelete =
        ['OWNER', 'SUPER_ADMIN'].includes(user?.role) ||
        Boolean(user?.permissions?.manage_reports);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    useEffect(() => {
        if (id) {
            api.get(`/reports/${id}`)
                .then(response => {
                    setReport(response.data);
                })
                .catch(error => {
                    console.error('Error fetching report:', error);
                    setReport(null);
                });
        }
    }, [id]);

    /**
     * 점검 기록 지우기.
     *
     * 실제로 지우지 않고 표시만 남긴다. 점검 기록은 무슨 일이 있었는지에 대한
     * 증거라, 잘못 지웠거나 악의로 지웠을 때 되돌릴 수 있어야 한다.
     */
    const remove = async () => {
        const ok = await showConfirm(
            '기록 삭제',
            '이 점검 기록을 지울까요? 이 기록으로 채워졌던 회차는 다시 비어 있는 것으로 돌아갑니다.',
            'danger',
        );
        if (!ok) return;
        try {
            await api.delete(`/reports/${id}`);
            navigate(-1);
        } catch (err) {
            showAlert('오류', '지우지 못했습니다: ' + (err.response?.data?.message || err.message), 'danger');
        }
    };

    if (!report) {
        return <div className="container mt-4"><div className="alert alert-warning">점검 기록을 찾을 수 없습니다.</div></div>;
    }

    return (
        <div className="inspection-detail-page bg-light min-vh-100 pb-5">
            <style>{`
        .content-card {
          background: white;
          border-radius: 20px;
          padding: 1.25rem 1rem;
          margin: 1rem 0.5rem;
          box-shadow: 0 4px 20px rgba(0,0,0,0.03);
        }
        .photo-grid img {
          width: 100%;
          aspect-ratio: 1;
          object-fit: cover;
          border-radius: 12px;
          border: 1px solid #eee;
        }
      `}</style>

            {/* Header */}
            <div className="page-title-bar">
                <button className="btn border-0 p-0 me-3" onClick={() => navigate(-1)}>
                    <i className="bi bi-chevron-left fs-4"></i>
                </button>
                <h5 className="mb-0 fw-bold">점검 상세 기록</h5>
                {canDelete && (
                    <button
                        className="btn btn-sm border-0 p-0 ms-auto text-danger"
                        onClick={remove}
                        title="이 기록 지우기"
                    >
                        <i className="bi bi-trash"></i>
                    </button>
                )}
            </div>

            {/* Info Card */}
            <div className="content-card">
                <div className="d-flex justify-content-between align-items-start mb-4">
                    <div>
                        <h4 className="fw-bold mb-1">{report.facility?.name || '시설 정보 없음'}</h4>
                        <div className="text-muted small">{report.section?.name || '구역 정보 없음'}</div>
                    </div>
                    <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-10 px-3 py-2 rounded-3">
                        점검 완료
                    </span>
                </div>

                <div className="mb-4">
                    <label className="fw-bold text-muted small mb-2 uppercase">점검 결과 요약</label>
                    <div className="bg-light p-3 rounded-4" style={{ minHeight: '100px', lineHeight: '1.6' }}>
                        <div className="mb-2"><i className="bi bi-clipboard-check text-primary me-2"></i></div>
                        {formatDescription(report.description)}
                    </div>
                </div>

                {report.photoUrls && report.photoUrls.length > 0 && (
                    <div className="mb-4">
                        <label className="fw-bold text-muted small mb-3 uppercase">첨부 사진 ({report.photoUrls.length})</label>
                        <div className="row g-2 photo-grid">
                            {report.photoUrls.map((url, index) => (
                                <div key={index} className="col-4 col-md-3">
                                    <div
                                        className="position-relative ratio ratio-1x1"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            setCurrentImageIndex(index);
                                            setViewerOpen(true);
                                        }}
                                    >
                                        <img src={url} alt={`Inspection ${index}`} className="w-100 h-100 rounded-3 object-fit-cover border" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="border-top pt-4">
                    <div className="row g-3">
                        <div className="col-6">
                            <label className="fw-bold text-muted d-block small mb-1">점검자</label>
                            <span className="fw-bold">{report.reporter?.name || '알 수 없음'}</span>
                        </div>
                        <div className="col-6">
                            <label className="fw-bold text-muted d-block small mb-1">유형</label>
                            <span className="badge bg-light text-dark border">정기 점검</span>
                        </div>
                        <div className="col-12">
                            <label className="fw-bold text-muted d-block small mb-1">점검 일시</label>
                            <div className="text-muted small">
                                {new Date(report.createdAt).toLocaleString()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ImageViewerModal
                show={viewerOpen}
                onHide={() => setViewerOpen(false)}
                images={report?.photoUrls || []}
                initialIndex={currentImageIndex}
            />
        </div>
    );
}

export default InspectionDetailPage;
